<template>
  <div class="file-previewer">
    <div v-if="!fileData" class="loading-state">
      <el-empty description="无文件数据"></el-empty>
    </div>
    
    <div v-else class="preview-content">
      <!-- 文本内容预览 -->
      <div v-if="!fileData.file_path && !fileData.file_url && fileData.content" class="text-preview">
        <h3 class="preview-title">内容预览</h3>
        <div v-html="fileData.content" class="text-content"></div>
      </div>

      <!-- 文件预览 -->
      <div v-if="fileData.file_path || fileData.file_url" class="file-preview-container">
        <h3 class="preview-title">文件预览</h3>
        
        <!-- 图片预览 -->
        <div v-if="fileData.file_type === 'image'" class="image-preview">
          <EnhancedImageViewer 
            :src="resolvedFileUrl"
            :alt="fileData.title || '预览图片'"
            :initial-scale="1.0"
            :auto-fit-to-screen="true"
          />
        </div>

        <!-- 视频预览 -->
        <div v-else-if="fileData.file_type === 'video'" class="video-container">
          <EnhancedVideoPlayer 
            :src="resolvedFileUrl"
            :poster="videoPoster"
            :autoplay="false"
            :loop="false"
            :initial-volume="0.8"
          />
        </div>

        <!-- 音频预览 -->
        <div v-else-if="fileData.file_type === 'audio'" class="audio-container">
          <EnhancedAudioPlayer 
            :src="resolvedFileUrl"
            :initial-volume="0.8"
            :auto-play="false"
            :show-visualizer="true"
          />
        </div>

        <!-- 文档预览 -->
        <div v-else-if="fileData.file_type === 'document'" class="document-container">
          <EnhancedDocumentViewer 
            :src="resolvedFileUrl"
            :file-name="fileData.file_name || fileData.title || 'document'"
            :file-format="fileData.file_format"
            @download="$emit('download')"
          />
        </div>

        <!-- 其他文件类型 -->
        <div v-else class="other-file">
          <div class="unsupported-content">
            <el-icon class="icon-large"><Document /></el-icon>
            <p>该文件类型不支持在线预览</p>
            <el-button type="primary" @click="$emit('download')">
              <el-icon><Download /></el-icon>
              下载文件
            </el-button>
          </div>
        </div>
      </div>
      
      <!-- 无可预览内容 -->
      <div v-if="!fileData.file_path && !fileData.file_url && !fileData.content" class="no-preview">
        <el-empty description="无可预览的内容" />
      </div>
    </div>
  </div>
</template>

<script>
import { ElTag, ElMessage } from 'element-plus'
import { Picture, Document, Download } from '@element-plus/icons-vue'
import EnhancedVideoPlayer from './EnhancedVideoPlayer.vue'
import EnhancedImageViewer from './EnhancedImageViewer.vue'
import EnhancedAudioPlayer from './EnhancedAudioPlayer.vue'
import EnhancedDocumentViewer from './EnhancedDocumentViewer.vue'

export default {
  name: 'FilePreviewer',
  components: {
    ElTag,
    Picture,
    Document,
    Download,
    EnhancedVideoPlayer,
    EnhancedImageViewer,
    EnhancedAudioPlayer,
    EnhancedDocumentViewer
  },
  props: {
    fileData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      textFileContent: '',
      previewComponent: 'el-image',
      videoPoster: ''
    }
  },
  computed: {
    resolvedFileUrl() {
      if (!this.fileData) return ''
      const candidate = this.fileData.file_url || this.fileData.filePath || this.fileData.file_path
      const fileType = this.fileData.file_type
      return this.getFileUrl(candidate, fileType)
    }
  },
  watch: {
    fileData: {
      handler(newFileData) {
        if ((newFileData.file_path || newFileData.file_url) && ['txt', 'md'].includes(newFileData.file_format)) {
          this.loadTextFileContent()
        }
        if (newFileData.file_type === 'video') {
          this.videoPoster = this.generateVideoPoster()
        }
      },
      immediate: true
    }
  },
  methods: {
    // 获取文件URL - 为 iframe/播放器 构建完整 URL。视频/音频可走 CDN（VUE_APP_CDN_MEDIA_BASE_URL）
    getFileUrl(filePath, fileType) {
      if (!filePath) return ''
      if (filePath.startsWith('http://') || filePath.startsWith('https://')) return filePath

      const pathPart = filePath.startsWith('/api/') ? filePath
        : filePath.startsWith('/uploads/') ? `/api${filePath}`
        : filePath.startsWith('uploads/') ? `/api/${filePath}`
        : filePath.startsWith('/documents/') ? `/api${filePath}`
        : filePath.startsWith('/') ? filePath
        : `/api/uploads/${String(filePath).replace(/^\/+/, '')}`
      const cdnBase = process.env.VUE_APP_CDN_MEDIA_BASE_URL || ''
      const isMedia = fileType === 'video' || fileType === 'audio'
      if (cdnBase && isMedia && pathPart.startsWith('/api/')) {
        return cdnBase.replace(/\/$/, '') + pathPart
      }
      return window.location.origin + pathPart
    },

    // 获取绝对文件URL（用于Office Web Viewer）
    getAbsoluteFileUrl(filePath) {
      const resolved = this.getFileUrl(filePath)
      if (resolved.startsWith('http://') || resolved.startsWith('https://')) {
        return resolved
      }
      const baseUrl = window.location.origin
      return `${baseUrl}${resolved}`
    },

    // 加载文本文件内容
    async loadTextFileContent() {
      try {
        const targetUrl = this.resolvedFileUrl
        if (!targetUrl) {
          throw new Error('无法解析文本文件地址')
        }
        const response = await fetch(targetUrl)
        if (!response.ok) {
          throw new Error('Failed to load text file')
        }
        this.textFileContent = await response.text()
      } catch (error) {
        console.error('加载文本文件失败:', error)
        ElMessage.error('加载文本文件内容失败')
      }
    },
    
    // 生成视频封面图URL
    generateVideoPoster() {
      // 在实际应用中，这里可以调用后端API生成视频封面
      // 或者使用视频URL的第一帧作为封面
      // 这里我们简单地返回一个空字符串，表示不使用封面图
      return ''
    },
    
    // 处理图片点击功能已在EnhancedImageViewer组件中实现
    handleImageClick() {
      // 保留此方法以确保兼容性
    }
  }
}
</script>

<style scoped>
.file-previewer {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.preview-content {
  border-radius: 8px;
  overflow: hidden;
  background-color: #fff;
  width: 100%;
  height: 100%;
}

.preview-title {
  padding: 15px 20px;
  margin: 0;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 16px;
  color: #303133;
}

.text-preview {
  padding: 20px;
}

.text-content {
  line-height: 1.6;
  color: #303133;
  white-space: pre-wrap;
  word-break: break-word;
}

.file-preview-container {
  /* 文件预览容器样式 */
}

.preview-media {
  max-width: 100%;
  display: block;
  margin: 0 auto;
}

.image-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;
}

.video-container {
  padding: 20px;
  text-align: center;
}

.video-container video {
  max-height: 500px;
  width: 100%;
  background-color: #000;
  border-radius: 4px;
}

.audio-container {
  padding: 20px;
}

.audio-container audio {
  width: 100%;
}

.document-container {
  /* 文档容器样式 */
}

.pdf-container {
  padding: 20px;
}

.pdf-frame {
  width: 100%;
  height: 600px;
  border: none;
  border-radius: 4px;
}

.office-preview {
  padding: 20px;
}

.office-frame {
  width: 100%;
  height: 600px;
  border: none;
  border-radius: 4px;
}

.text-file-container {
  padding: 20px;
}

.text-file-content {
  background-color: #f5f5f5;
  padding: 15px;
  border-radius: 4px;
  max-height: 500px;
  overflow-y: auto;
  font-family: monospace;
}

.text-file-content pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.unsupported-document,
.other-file {
  padding: 40px 20px;
  text-align: center;
}

.unsupported-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  color: #909399;
}

.icon-large {
  font-size: 48px;
}

@media (max-width: 768px) {
  .pdf-frame,
  .office-frame {
    height: 400px;
  }
}
</style>